import {
  CSSProperties,
  FormEvent,
  forwardRef,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
} from 'react'
import { useForm, FormProvider, UseFormReturn, Resolver } from 'react-hook-form'
import { isArray, merge, omit } from 'lodash-es'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppStore } from '@stores'
import StoreSubscription from './StoreSubscription'

interface FieldSubscription {
  name: string
  value: string | number
}

interface Props {
  onSubmit: (
    values: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    event: FormEvent<HTMLFormElement>,
    isDirty: boolean,
    touchedFields: Record<string, unknown>,
  ) => void | Promise<void | unknown>
  children?: ReactNode | ((methods: UseFormReturn) => ReactNode)
  id?: string
  validationSchema?: any // eslint-disable-line @typescript-eslint/no-explicit-any
  validationContext?: any // eslint-disable-line @typescript-eslint/no-explicit-any
  defaultValues?: { [key: string]: any | undefined } // eslint-disable-line @typescript-eslint/no-explicit-any
  /**
   * @deprecated try to use reacts native "key" instead in order to force component to rerender when default values change
   */
  enableReinitialize?: boolean
  'data-cy'?: string
  fieldSubscriptions?: FieldSubscription[] | FieldSubscription
  storeSubscription?: StoreSubscription
  style?: CSSProperties
  resetIsDirtyAfterSubmit?: boolean
  noValidate?: boolean
  /**
   * Keys that should be preserved on reinitialize, so their values will not be
   * reset to default
   */
  preserveOnReinitialize?: string[]
}

const emptyArray: string[] = []

const Form = forwardRef(function (
  {
    onSubmit,
    children,
    defaultValues = {},
    validationSchema,
    id = '',
    enableReinitialize = false,
    validationContext,
    'data-cy': dataCy,
    fieldSubscriptions,
    storeSubscription,
    style,
    resetIsDirtyAfterSubmit = false,
    noValidate = false,
    preserveOnReinitialize = emptyArray,
  }: Props,
  ref,
): ReactElement {
  const disableFormValidation = useAppStore(state => state.disableFormValidation)

  const methods: UseFormReturn = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
    context: validationContext,
    ...(validationSchema && !disableFormValidation
      ? { resolver: yupResolver(validationSchema) as unknown as Resolver }
      : {}),
  })

  useEffect(() => {
    if (enableReinitialize) {
      if (preserveOnReinitialize.length > 0) {
        const currentValues = methods.getValues()
        const defaultsWithoutPreserved = omit(defaultValues, preserveOnReinitialize)
        const newValues = merge(currentValues, defaultsWithoutPreserved)
        methods.reset(newValues)
      } else {
        methods.reset(defaultValues)
      }
    }
  }, [defaultValues, enableReinitialize, preserveOnReinitialize])

  useEffect(() => {
    if (!fieldSubscriptions) return

    if (!isArray(fieldSubscriptions)) {
      methods.setValue(fieldSubscriptions.name, fieldSubscriptions.value)
    }

    if (isArray(fieldSubscriptions)) {
      fieldSubscriptions.forEach(({ name, value }) => {
        methods.setValue(name, value)
      })
    }
  }, [fieldSubscriptions])

  const isDirty = useMemo(() => methods.formState.isDirty, [methods.formState.isDirty])

  const touchedFields = useMemo(
    () => methods.formState.touchedFields,
    [methods.formState.touchedFields],
  )

  return (
    <FormProvider {...methods}>
      {storeSubscription && <StoreSubscription {...storeSubscription} />}
      <form
        id={id}
        data-cy={dataCy || id}
        onSubmit={event => {
          // Doint most of the code twice in order to not break anything in this
          // cycle. I think the lower method can be removed once it proves to work
          if (resetIsDirtyAfterSubmit) {
            if (onSubmit) {
              event.stopPropagation()
              methods.handleSubmit(async data => {
                await onSubmit(data, event, isDirty, touchedFields)
                if (resetIsDirtyAfterSubmit) {
                  methods.reset(data, { keepValues: true })
                }
              })(event)
            }
          } else {
            if (onSubmit) {
              event.stopPropagation()
              methods.handleSubmit(data => onSubmit(data, event, isDirty, touchedFields))(event)
            }
          }
        }}
        // @ts-expect-error
        ref={ref}
        style={style}
        noValidate={noValidate}
      >
        {typeof children === 'function' ? children(methods) : children}
      </form>
    </FormProvider>
  )
})

Form.displayName = 'Form'

export default Form
